/**
 * Copyright (c) 2024 rports (r), All rights reserved.
 *
 *     ______    _______  _______  ______    _______  _______ 
 *    |    _ |  |       ||       ||    _ |  |       ||       |
 *    |   | ||  |    _  ||   _   ||   | ||  |_     _||  _____|
 *    |   |_||_ |   |_| ||  | |  ||   |_||_   |   |  | |_____ 
 *    |    __  ||    ___||  |_|  ||    __  |  |   |  |_____  |
 *    |   |  | ||   |    |       ||   |  | |  |   |   _____| |
 *    |___|  |_||___|    |_______||___|  |_|  |___|  |_______|
 *
 * filename      : Footer.jsx
 * description   : {{insert here}}
 * creation date : 02/01/2024
 * author        : "Luis CRUZ" <luis.cruz@rports.net>
 */



// --------------------------------------------------------------------------------
//      IMPORTS
// --------------------------------------------------------------------------------

import {
    Fragment,
} from 'react';

import {
    I18n,
} from '@aws-amplify/core';

import {
    View,
    Flex,
    Text,
    Image,
    Heading,
} from '@aws-amplify/ui-react';

import {
    id_list,
} from '@/utils/router/core';

import {
    useComponentContext,
    toolKit,
} from '@/components/core';

import {
    LinkText,
} from '@/components/snippets/RouterLink';

import {
    locale_msg,
} from '@/utils/locale/enums';

// Images
import CountryFlagFrance from 'svg-country-flags/svg/fr.svg';
import IconHeart from '@/assets/images/various/heart.svg';
import BrandLogo from '@/assets/images/logo/square-rounded-background/default/medium.png';



// --------------------------------------------------------------------------------
//      FUNCTIONS
// --------------------------------------------------------------------------------

function Footer({ classPrefix, configPages }) {
    // Component context
    const { ctx } = useComponentContext();

    // Current date
    const now = new Date();

    // Compute copyright period
    var date_period = (('' + now.getFullYear()) === process.env.REACT_APP__FOUNDATION_DATE__YEAR) ?
        now.getFullYear() :
        process.env.REACT_APP__FOUNDATION_DATE__YEAR + '-' + now.getFullYear();

    // Filtered configuration pages
    const filtered_config_pages = [];

    for (let i = 0; i < configPages.length; i++) {
        var cnt = 0;

        // Walk the dictionary
        for (let j = 0; j < configPages[i].dict.length; j++) {
            if (true === ctx.api.location.canBeDisplayed(configPages[i].dict[j])) {
                cnt++;
            }
        }

        // Add the config to the filtered list, if allowed
        if (0 < cnt) {
            filtered_config_pages.push(configPages[i]);
        }
    }

    return (
        <Fragment>
            <View
                id={id_list.FOOTER}
                paddingTop={
                    ctx.api.utils.adaptiveOption(
                        ctx.theme.tokens.space.medium,
                        ctx.theme.tokens.space.zero,
                        ctx.theme.tokens.space.small
                    )
                }
                className={`${classPrefix}`}
            >
                <View
                    className={`${classPrefix}-container`}
                >
                    <Flex
                        direction='column'
                        gap={ctx.theme.tokens.space.small}
                        textAlign='center'
                    >
                        <Flex
                            direction={
                                ctx.api.utils.adaptiveOption(
                                    'row',
                                    'column',
                                    'column'
                                )
                            }
                            width={ctx.theme.tokens.space.relative.full}
                            gap={ctx.theme.tokens.space.zero}
                        >
                            <Flex
                                wrap='wrap'
                                justifyContent={
                                    ctx.api.utils.adaptiveOption(
                                        'left',
                                        'center',
                                        'left'
                                    )
                                }
                                direction={
                                    ctx.api.utils.adaptiveOption(
                                        'row',
                                        'row',
                                        'row'
                                    )
                                }
                                width={ctx.theme.tokens.space.relative.full}
                                gap={ctx.theme.tokens.space.zero}
                                paddingBottom={
                                    ctx.api.utils.adaptiveOption(
                                        ctx.theme.tokens.space.zero,
                                        ctx.theme.tokens.space.small,
                                        ctx.theme.tokens.space.large
                                    )
                                }
                            >
                                {filtered_config_pages.map((item, i) => {
                                    return (
                                        <Fragment
                                            key={i}
                                        >
                                            <View
                                                width={
                                                    ctx.api.utils.adaptiveOption(
                                                        '50%',
                                                        '33.333%',
                                                        '50%'
                                                    )
                                                }
                                                textAlign='left'
                                            >
                                                <View
                                                    margin={
                                                        ctx.api.utils.adaptiveOption(
                                                            ctx.theme.tokens.space.large,
                                                            ctx.theme.tokens.space.large,
                                                            ctx.theme.tokens.space.small
                                                        )
                                                    }
                                                    className={`${classPrefix}-text`}
                                                >
                                                    <Heading
                                                        level={
                                                            ctx.api.utils.adaptiveOption(
                                                                4,
                                                                4,
                                                                5
                                                            )
                                                        }
                                                        paddingBottom={ctx.theme.tokens.space.medium}
                                                    >
                                                        {item.label}
                                                    </Heading>

                                                    <Flex
                                                        direction='column'
                                                        gap={ctx.theme.tokens.space.xs}
                                                        className={`${classPrefix}-text`}
                                                    >
                                                        {item.dict.map((page, j) => {
                                                            // Detect private access
                                                            if (false === ctx.api.location.canBeDisplayed(page)) {
                                                                return null;
                                                            }

                                                            return (
                                                                <Fragment
                                                                    key={j}
                                                                >
                                                                    <LinkText
                                                                        to={
                                                                            toolKit.api.location.buildUrl(
                                                                                page.path,
                                                                                page.search,
                                                                                page.hash
                                                                            )
                                                                        }
                                                                    >
                                                                        {page.label.footer}
                                                                    </LinkText>
                                                                </Fragment>
                                                            );
                                                        })}
                                                    </Flex>
                                                </View>
                                            </View>
                                        </Fragment>
                                    );
                                })}
                            </Flex>

                            <Flex
                                direction={
                                    ctx.api.utils.adaptiveOption(
                                        'row',
                                        'row',
                                        'column'
                                    )
                                }
                                width={ctx.theme.tokens.space.relative.full}
                                gap={ctx.theme.tokens.space.zero}
                                justifyContent={
                                    ctx.api.utils.adaptiveOption(
                                        '',
                                        'center',
                                        ''
                                    )
                                }
                                paddingTop={
                                    ctx.api.utils.adaptiveOption(
                                        ctx.theme.tokens.space.zero,
                                        ctx.theme.tokens.space.zero,
                                        ctx.theme.tokens.space.medium
                                    )
                                }
                            >
                                <View
                                    width={
                                        ctx.api.utils.adaptiveOption(
                                            ctx.theme.tokens.space.relative.full,
                                            '20%',
                                            ctx.theme.tokens.space.relative.full
                                        )
                                    }
                                    textAlign={
                                        ctx.api.utils.adaptiveOption(
                                            'right',
                                            'right',
                                            'center'
                                        )
                                    }
                                >
                                    <Image
                                        src={BrandLogo}
                                        maxHeight='10rem'
                                        padding={ctx.theme.tokens.space.small}
                                    />
                                </View>

                                <View
                                    width={
                                        ctx.api.utils.adaptiveOption(
                                            ctx.theme.tokens.space.relative.full,
                                            '40%',
                                            ctx.theme.tokens.space.relative.full
                                        )
                                    }
                                    textAlign={
                                        ctx.api.utils.adaptiveOption(
                                            'left',
                                            'left',
                                            'center'
                                        )
                                    }
                                    className={`${classPrefix}-text`}
                                >
                                    <Text
                                        paddingLeft={
                                            ctx.api.utils.adaptiveOption(
                                                ctx.theme.tokens.space.small,
                                                ctx.theme.tokens.space.small,
                                                ctx.theme.tokens.space.zero
                                            )
                                        }
                                        fontFamily='Cera Pro'
                                        fontSize='3rem'
                                        fontWeight={ctx.theme.tokens.fontWeights.extrabold}
                                        lineHeight={ctx.theme.tokens.lineHeights.small}
                                    >
                                        {process.env.REACT_APP__PROJECT__NAME.toLocaleUpperCase()}
                                    </Text>

                                    <Text
                                        paddingLeft={
                                            ctx.api.utils.adaptiveOption(
                                                ctx.theme.tokens.space.small,
                                                ctx.theme.tokens.space.small,
                                                ctx.theme.tokens.space.zero
                                            )
                                        }
                                        fontFamily='Cera Pro'
                                        fontSize='2.2rem'
                                        fontWeight={ctx.theme.tokens.fontWeights.bold}
                                        lineHeight={ctx.theme.tokens.lineHeights.small}
                                    >
                                        {process.env.REACT_APP__SITE__SLOGAN}
                                    </Text>
                                </View>
                            </Flex>
                        </Flex>

                        <Flex
                            direction='column'
                            gap={ctx.theme.tokens.space.zero}
                            paddingTop={
                                ctx.api.utils.adaptiveOption(
                                    ctx.theme.tokens.space.large,
                                    ctx.theme.tokens.space.xl,
                                    ctx.theme.tokens.space.xxl
                                )
                            }
                            paddingBottom={ctx.theme.tokens.space.xxl}
                        >
                            <View
                                width={ctx.theme.tokens.space.relative.full}
                                className={`${classPrefix}-text`}
                            >
                                <Flex
                                    direction='row'
                                    justifyContent='center'
                                    alignItems='center'
                                    alignContent='center'
                                    wrap='wrap'
                                    gap={ctx.theme.tokens.space.zero}
                                >
                                    <Text>
                                        &copy;&nbsp;{date_period}&nbsp;
                                    </Text>

                                    <LinkText
                                        to={window.location.origin}
                                    >
                                        {process.env.REACT_APP__PROJECT__NAME.toLocaleLowerCase()}
                                    </LinkText>

                                    <Text>
                                        &nbsp;&reg;,&nbsp;
                                    </Text>

                                    <Text>
                                        {I18n.get(locale_msg.GENERAL__COPYRIGHT) + '.'}
                                    </Text>
                                </Flex>
                            </View>

                            <View
                                width={ctx.theme.tokens.space.relative.full}
                                className={`${classPrefix}-text`}
                            >
                                <Flex
                                    direction='row'
                                    justifyContent='center'
                                    alignItems='center'
                                    alignContent='center'
                                    wrap='wrap'
                                    gap={ctx.theme.tokens.space.zero}
                                >
                                    <Text>
                                        {'Powered by'}&nbsp;
                                    </Text>

                                    <LinkText
                                        to={
                                            toolKit.api.location.buildUrl(
                                                process.env.REACT_APP__PARTNER__STAURIX__URL
                                            )
                                        }
                                        isExternal={true}
                                    >
                                        {process.env.REACT_APP__PARTNER__STAURIX__NAME.toLocaleLowerCase()}
                                    </LinkText>

                                    <Text>
                                        .
                                    </Text>
                                </Flex>
                            </View>

                            <View
                                width={ctx.theme.tokens.space.relative.full}
                            >
                                <View
                                    as='span'
                                    className={`${classPrefix}-badge`}
                                >
                                    Made in&nbsp;

                                    <Image
                                        src={CountryFlagFrance}
                                        height={ctx.theme.tokens.space.small}
                                    />

                                    &nbsp;with&nbsp;

                                    <Image
                                        src={IconHeart}
                                        height={ctx.theme.tokens.space.small}
                                    />
                                </View>
                            </View>
                        </Flex>
                    </Flex>
                </View>
            </View>
        </Fragment>
    );
}



// --------------------------------------------------------------------------------
//      EXPORTED OBJECTS
// --------------------------------------------------------------------------------

export default Footer;



// End of file
